import {
  BarChart3,
  CalendarDays,
  CalendarPlus,
  ClipboardList,
  FileText,
  History,
  IndianRupee,
  Package,
  Receipt,
  Settings,
  ShieldAlert,
  Users,
  UsersRound,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { UserRole } from '@/domain';

/**
 * Navigation model.
 *
 * `roles` mirrors the permission matrix in FRD §3.2. Filtering here keeps Staff from
 * seeing doors they cannot open — the API refuses them regardless (Task 06).
 */

/**
 * The application's name, in one place.
 *
 * Distinct from the `company_name` setting, which is the studio's registered trading name
 * and prints on invoices, bills and receipts. This is what the software calls itself; that
 * is what the business calls itself. Conflating them would mean a rebrand of one silently
 * rewrote the other, and only one of the two belongs on a tax document.
 */
export const APP_NAME = 'Event Media';

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  roles?: UserRole[];
  /** Match child routes too, e.g. /customers/12 highlights Customers. */
  matchPrefix?: boolean;
}

export interface NavSection {
  heading: string;
  /** Show the links directly, with no heading or fold — for the always-needed destinations. */
  flat?: boolean;
  items: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    heading: 'Overview',
    flat: true,
    items: [
      { label: 'Dashboard', to: '/', icon: BarChart3 },
      { label: 'Quick Booking', to: '/quick-booking', icon: CalendarPlus },
    ],
  },
  {
    heading: 'Bookings',
    items: [
      { label: 'Customers', to: '/customers', icon: UsersRound, matchPrefix: true },
      { label: 'Events', to: '/events', icon: CalendarDays, matchPrefix: true },
    ],
  },
  {
    heading: 'Billing',
    items: [
      { label: 'Invoices', to: '/invoices', icon: FileText, matchPrefix: true },
      { label: 'Bills', to: '/bills', icon: Receipt, matchPrefix: true },
      { label: 'Payments', to: '/payments', icon: IndianRupee, matchPrefix: true },
      { label: 'Services', to: '/services', icon: Package },
    ],
  },
  {
    heading: 'Reports',
    items: [
      { label: 'Outstanding Dues', to: '/reports/outstanding', icon: ClipboardList },
      { label: 'Retention Watch', to: '/reports/retention', icon: ShieldAlert },
    ],
  },
  {
    heading: 'Administration',
    items: [
      { label: 'Staff Accounts', to: '/users', icon: Users, roles: ['ADMIN'] },
      { label: 'Settings', to: '/settings', icon: Settings, roles: ['ADMIN'] },
      { label: 'Audit Log', to: '/audit-logs', icon: History, roles: ['ADMIN'] },
    ],
  },
];

/** Sections with no items visible to this role are dropped entirely. */
export function visibleSections(role: UserRole | undefined): NavSection[] {
  if (!role) return [];

  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.roles || item.roles.includes(role)),
  })).filter((section) => section.items.length > 0);
}
